import { z } from 'zod';
/** Tenant intent + artifact provenance enum */
export const executionPostureIntentSchema = z.enum(['cloud_worker', 'hybrid_worker']);
export const hybridReadinessStateSchema = z.enum(['not_configured', 'onboarding', 'operational']);
export const hybridHealthClassificationSchema = z.enum([
    'operational',
    'degraded',
    'unavailable',
    'unknown',
]);
/** Top-level artifact provenance label */
export const executionPostureProvenanceSchema = z.enum(['cloud_worker', 'hybrid_worker']);
function failClosedForHybridScope(classification) {
    return classification !== 'operational';
}
function executionExpectationCopy(failClosed) {
    if (!failClosed) {
        return 'Hybrid-scoped automation may proceed only while pathway health remains operational. CodeMerlin does not silently move hybrid-bound workloads to the vendor Cloud Worker unless your organization explicitly configures a different policy.';
    }
    return 'Hybrid-scoped automation is paused until pathway health returns to operational. CodeMerlin does not silently fall back to the vendor Cloud Worker for hybrid-bound workloads unless your organization explicitly configures a different policy.';
}
/**
 * Pure health aggregate — production-safe `unknown` when signals are absent (research R4).
 * Non-production defaults to operational so local/integration paths stay usable.
 * Tests may set `CODE_MERLIN_HYBRID_HEALTH_STUB` to degraded|unavailable|healthy|none when NODE_ENV is not production.
 */
export function evaluateHybridWorkerHealth(input) {
    const now = new Date();
    const stubRaw = input.signalStub ??
        (input.nodeEnv !== 'production'
            ? process.env['CODE_MERLIN_HYBRID_HEALTH_STUB']
            : undefined);
    const stub = stubRaw === 'healthy' ||
        stubRaw === 'degraded' ||
        stubRaw === 'unavailable' ||
        stubRaw === 'none'
        ? stubRaw
        : undefined;
    if (stub === 'healthy') {
        return {
            classification: 'operational',
            customer_safe_summary: 'Hybrid pathway checks reported operational in this configured test scenario.',
            last_successful_health_observation_at: now,
            evaluated_at: now,
        };
    }
    if (stub === 'degraded') {
        return {
            classification: 'degraded',
            customer_safe_summary: 'Hybrid pathway checks reported a partial degradation in this configured test scenario.',
            last_successful_health_observation_at: now,
            evaluated_at: now,
        };
    }
    if (stub === 'unavailable') {
        return {
            classification: 'unavailable',
            customer_safe_summary: 'Hybrid pathway checks reported an unavailable state in this configured test scenario.',
            last_successful_health_observation_at: null,
            evaluated_at: now,
        };
    }
    if (input.nodeEnv === 'production') {
        return {
            classification: 'unknown',
            customer_safe_summary: 'Hybrid pathway health is not yet observable for this organization; treat hybrid-bound automation as paused until an administrator confirms operational status.',
            last_successful_health_observation_at: null,
            evaluated_at: now,
        };
    }
    if (stub === 'none') {
        return {
            classification: 'unknown',
            customer_safe_summary: 'Hybrid pathway health signals are intentionally withheld in this test configuration.',
            last_successful_health_observation_at: null,
            evaluated_at: now,
        };
    }
    return {
        classification: 'operational',
        customer_safe_summary: 'In non-production environments, hybrid pathway health defaults to operational when live telemetry is not configured.',
        last_successful_health_observation_at: now,
        evaluated_at: now,
    };
}
export function resolveExecutionPostureForProvenance(input) {
    if (input.executionPostureIntent === 'cloud_worker') {
        return 'cloud_worker';
    }
    if (input.hybridReadinessState === 'operational' &&
        input.hybridHealthClassification === 'operational') {
        return 'hybrid_worker';
    }
    return 'cloud_worker';
}
export const RESIDENCY_NARRATIVE_CLOUD_WORKER = {
    headline: 'Reviews run in CodeMerlin’s vendor-operated execution boundary',
    body: 'For this organization, primary analysis and automation run in CodeMerlin’s vendor-hosted execution plane. Customer-managed hybrid compute is not the active execution path unless your administrator switches intent and completes hybrid readiness.',
};
export const RESIDENCY_NARRATIVE_HYBRID_WORKER = {
    headline: 'This organization targets customer-managed hybrid execution for applicable workloads',
    body: 'Hybrid Worker posture means selected automation can run in a customer-managed boundary once hybrid readiness is operational and pathway health is operational. Readiness (lifecycle setup) and health (live signals) are independent—follow this summary for both.',
};
const hybridWorkerSurfaceSchema = z.object({
    readiness_state: hybridReadinessStateSchema,
    health_classification: hybridHealthClassificationSchema,
    last_successful_health_observation_at: z.string().datetime().nullable(),
    fail_closed_for_hybrid_scope: z.boolean(),
    health_customer_summary: z.string(),
    execution_expectation_customer_summary: z.string(),
    partial_scope_qualifier: z.string().nullable().optional(),
});
export const executionPostureSummarySchema = z.object({
    execution_posture_intent: executionPostureIntentSchema,
    residency_headline: z.string(),
    residency_body: z.string(),
    hybrid_worker: hybridWorkerSurfaceSchema.nullable(),
    member_access_note: z.string(),
});
export function buildHybridCustomerSummaries(classification, options) {
    const failClosed = failClosedForHybridScope(classification);
    const qualifier = options?.scopeQualifier && options.scopeQualifier.trim().length > 0
        ? ` Scope note: ${options.scopeQualifier.trim()}`
        : '';
    const baseHealth = {
        operational: 'Hybrid pathway health is operational. Hybrid-scoped automation may proceed while this remains true.',
        degraded: 'Hybrid pathway health is degraded. Hybrid-scoped automation should be treated as paused until administrators restore operational health.',
        unavailable: 'Hybrid pathway health is unavailable. Hybrid-scoped automation should remain paused until connectivity and registration are restored.',
        unknown: 'Hybrid pathway health cannot be confirmed from available signals. Hybrid-scoped automation should be treated as paused until administrators verify operational status.',
    };
    return {
        health_customer_summary: `${baseHealth[classification]}${qualifier}`,
        execution_expectation_customer_summary: executionExpectationCopy(failClosed),
        fail_closed_for_hybrid_scope: failClosed,
    };
}
//# sourceMappingURL=execution-posture.js.map